import { BUC_DEP_KEY, FULL_PAYMENT_DEP_KEY, PRE_BUC_DEP_KEY } from '../../office/deedPossessionGates';
import { yesAnswerStepKey } from '../../office/workflowAnswerGate';
import { STAGES, stageOfStep, type StageKey } from '../../office/unit/lifecycle';

/** Everything the workflow editor knows about steps, kept apart from the screen so it can be tested. */
export const normalizeKey = (value: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);

export const UNIT_BOOKED_DEP_KEY = '__unit_booked__';
export const LOAN_REQUIRED_DEP_KEY = '__loan_required_yes__';
export const GATE_LABELS: Record<string, string> = {
  [UNIT_BOOKED_DEP_KEY]: 'Unit booked',
  [LOAN_REQUIRED_DEP_KEY]: 'Loan required (Yes)',
  [FULL_PAYMENT_DEP_KEY]: '100% unit price received',
  [BUC_DEP_KEY]: 'BUC received for this building',
  [PRE_BUC_DEP_KEY]: 'Before BUC for this building',
};
export const isGate = (key: string) => key in GATE_LABELS || yesAnswerStepKey(key) !== null;

export const ROLE_OPTIONS = [
  { value: '', label: 'Anyone' },
  { value: 'super-admin', label: 'Admin' },
  { value: 'sales-manager', label: 'Sales Manager' },
  { value: 'sales-team', label: 'Sales Team' },
  { value: 'trainee', label: 'Trainee' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'legal', label: 'Legal' },
];

export const ACTIONS: Array<{ value: string; label: string; short: string; button: string }> = [
  { value: 'mark_done', label: 'Mark done', short: 'Mark done', button: 'Mark Done' },
  { value: 'upload_document', label: 'Upload a document', short: 'Upload', button: 'Upload' },
  { value: 'add_customer', label: 'Add customer', short: 'Add customer', button: 'Add Customer' },
  { value: 'add_kyc', label: 'Add KYC', short: 'Add KYC', button: 'Add KYC' },
  { value: 'add_payment', label: 'Add payment', short: 'Payment', button: 'Add Payment' },
  { value: 'generate_document', label: 'Generate a document', short: 'Generate', button: 'Generate' },
  { value: 'send_email', label: 'Send email', short: 'Email', button: 'Send Email' },
  { value: 'loan_sanction_form', label: 'Loan sanction form', short: 'Sanction form', button: 'Open Form' },
  { value: 'advocate_agreement_fee_receipt', label: 'Advocate sale agreement fee receipt', short: 'Advocate fee', button: 'Add Receipt' },
  { value: 'toggle_yes_no', label: 'Yes / No question', short: 'Yes / No', button: 'Yes / No' },
  { value: 'checklist', label: 'Checklist', short: 'Checklist', button: 'Checklist' },
  { value: 'add_bank_lead', label: 'Add bank lead', short: 'Bank lead', button: 'Add Lead' },
  { value: 'input_value', label: 'Input value', short: 'Input', button: 'Save' },
  { value: 'deed_charges_paid', label: 'Deed charges paid', short: 'Deed charges', button: 'Check' },
];
export const actionInfo = (v: string) => ACTIONS.find((a) => a.value === v) || ACTIONS[0];

export type WfStep = {
  key: string;
  title: string;
  actionType: string;
  inputType: string;
  enabled: boolean;
  required: boolean;
  showInOtherWorkflow: boolean;
  allowSkip: boolean;
  dueInDays: string | number;
  defaultRole: string;
  dependsOnSteps: string[];
  dependsMode: 'all' | 'any';
  helpText: string;
  showInDrawer: boolean;
  showInDashboard: boolean;
  keepActive: boolean;
  docKey: string;
  fieldKey: string;
  paymentType: string;
  stage: string;
  [extra: string]: any;
};

export const makeStep = (title = 'New step'): WfStep => ({
  key: normalizeKey(title) || 'step',
  title,
  actionType: 'mark_done',
  inputType: 'text',
  enabled: true,
  required: true,
  showInOtherWorkflow: false,
  allowSkip: false,
  dueInDays: '',
  defaultRole: '',
  dependsOnSteps: [],
  dependsMode: 'all',
  helpText: '',
  showInDrawer: true,
  showInDashboard: true,
  keepActive: false,
  docKey: '',
  fieldKey: '',
  paymentType: 'both',
  stage: '',
});

/** Saved steps (including the very old "subtasks" shape) into editor steps. */
export function normalizeIncomingSteps(input: any[]): WfStep[] {
  const raw = Array.isArray(input) ? input : [];
  const one = (s: any, deps: string[]): WfStep => ({
    ...makeStep(String(s?.title || s?.label || s?.key || 'Step')),
    ...s,
    key: String(s?.key || normalizeKey(String(s?.title || 'step')) || 'step'),
    title: String(s?.title || s?.label || s?.key || 'Step'),
    dependsOnSteps: deps,
    dependsMode: String(s?.dependsMode || 'all') === 'any' ? 'any' : 'all',
    actionType: String(s?.actionType || 'mark_done'),
    inputType: String(s?.inputType || 'text'),
    dueInDays: s?.dueInDays ?? '',
    defaultRole: String(s?.defaultRole || ''),
    helpText: String(s?.helpText || ''),
    docKey: String(s?.docKey || ''),
    fieldKey: String(s?.fieldKey || ''),
    paymentType: String(s?.paymentType || 'both'),
    stage: String(s?.stage || ''),
    enabled: s?.enabled !== false,
    required: !!s?.required,
    showInOtherWorkflow: !!s?.showInOtherWorkflow,
    allowSkip: !!s?.allowSkip,
    showInDrawer: s?.showInDrawer !== false,
    showInDashboard: s?.showInDashboard !== false,
    keepActive: !!s?.keepActive,
  });
  const depsOf = (s: any) => (Array.isArray(s?.dependsOnSteps) ? s.dependsOnSteps : Array.isArray(s?.dependsOn) ? s.dependsOn : []).filter(Boolean).map(String);
  if (!raw.some((s: any) => Array.isArray(s?.subtasks) && s.subtasks.length)) return raw.map((s) => one(s, depsOf(s)));
  const out: WfStep[] = [];
  raw.forEach((step: any) => {
    const subs = Array.isArray(step?.subtasks) ? step.subtasks : [];
    if (!subs.length) return void out.push(one(step, depsOf(step)));
    subs.forEach((sub: any, idx: number) =>
      out.push({
        ...one({ ...sub, key: sub?.key || normalizeKey(`${step?.key || 'step'}_${sub?.label || sub?.key || idx + 1}`), dueInDays: step?.dueInDays, defaultRole: step?.defaultRole, enabled: step?.enabled }, depsOf(step)),
        migratedFromLegacy: true,
        legacyStepKey: String(step?.key || ''),
        legacyStepTitle: String(step?.title || ''),
        legacySubtaskOrder: idx + 1,
      })
    );
  });
  return out;
}

const fieldKeyFor = (s: WfStep) => {
  const t = String(s.actionType || '').toLowerCase();
  if (t !== 'toggle_yes_no' && t !== 'checklist') return String(s.fieldKey || '').trim();
  return String(s.fieldKey || '').trim() || normalizeKey(String(s.title || s.key || '')) || String(s.key || '').trim();
};

/** The exact step shape saved to Firestore (same fields the previous editor saved, plus `stage`). */
export const stepsForSave = (steps: WfStep[]) =>
  steps.map((s, idx) => ({
    key: String(s.key || '').trim(),
    title: String(s.title || '').trim(),
    order: idx + 1,
    actionType: String(s.actionType || 'mark_done'),
    inputType: String(s.inputType || 'text'),
    enabled: s.enabled !== false,
    required: !!s.required,
    showInOtherWorkflow: !!s.showInOtherWorkflow,
    allowSkip: !!s.allowSkip,
    dueInDays: s.dueInDays !== '' && Number.isFinite(Number(s.dueInDays)) ? Number(s.dueInDays) : null,
    defaultRole: String(s.defaultRole || '').trim() || null,
    dependsOnSteps: (s.dependsOnSteps || []).map((d) => String(d || '').trim()).filter(Boolean),
    dependsMode: s.dependsMode === 'any' ? 'any' : 'all',
    helpText: String(s.helpText || '').trim() || null,
    showInDrawer: s.showInDrawer !== false,
    showInDashboard: s.showInDashboard !== false,
    keepActive: !!s.keepActive,
    docKey: String(s.docKey || '').trim() || null,
    fieldKey: fieldKeyFor(s) || null,
    paymentType: String(s.paymentType || '').trim() || null,
    stage: String(s.stage || '').trim() || null,
  }));

/** Problems per step index, plus workflow-level ones under -1. Same rules as the previous editor. */
export function validateWorkflow(name: string, steps: WfStep[]): Map<number, string> {
  const out = new Map<number, string>();
  if (!String(name || '').trim()) out.set(-1, 'Give the workflow a name.');
  else if (!steps.length) out.set(-1, 'Add at least one step.');
  const keys = new Set<string>();
  steps.forEach((s, i) => {
    const key = String(s.key || '').trim();
    const t = String(s.actionType || '').toLowerCase();
    let err = '';
    if (!key) err = 'Step key is required.';
    else if (keys.has(key)) err = `Another step already uses the key "${key}".`;
    else if (!String(s.title || '').trim()) err = 'Step title is required.';
    else if (t === 'input_value' && !String(s.fieldKey || '').trim()) err = 'Input value needs a field key.';
    else if (t === 'generate_document' && !String(s.docKey || '').trim()) err = 'Pick the document template to generate.';
    else if (t === 'send_email' && !String(s.docKey || '').trim()) err = 'Pick the email template to send.';
    keys.add(key);
    if (err) out.set(i, err);
  });
  steps.forEach((s, i) => {
    if (out.has(i)) return;
    const seen = new Set<string>();
    for (const raw of s.dependsOnSteps || []) {
      const d = String(raw || '').trim();
      if (!d) continue;
      if (seen.has(d)) return void out.set(i, `"${d}" is listed twice under Ready when.`);
      seen.add(d);
      const answerKey = yesAnswerStepKey(d);
      if (answerKey) {
        const questionIndex = steps.findIndex((candidate) => candidate.key === answerKey);
        if (questionIndex < 0) return void out.set(i, `The Yes answer refers to a missing step: "${answerKey}".`);
        if (questionIndex >= i) return void out.set(i, 'A Yes answer must come from an earlier step.');
        if (steps[questionIndex].actionType !== 'toggle_yes_no') return void out.set(i, 'A Yes answer must come from a Yes / No question.');
        continue;
      }
      if (isGate(d)) continue;
      if (d.includes(':')) continue;
      if (d === String(s.key || '').trim()) return void out.set(i, 'A step cannot wait for itself.');
      if (!keys.has(d)) return void out.set(i, `Waits for "${d}", which is not in this workflow.`);
    }
  });
  return out;
}

/** The stage a step shows under: the one picked, else worked out from its key and title like the unit page does. */
export function stagesFor(steps: WfStep[], type: 'main' | 'loan'): StageKey[] {
  let prev: StageKey | null = null;
  return steps.map((s) => {
    const k = stageOfStep({ workflowType: type, key: s.key, title: s.title, stage: s.stage }, prev);
    prev = k;
    return k;
  });
}
export const STAGE_OPTIONS = STAGES;

export const salesPreset = () => ({
  name: 'Sales Workflow (V2 - Step Only)',
  type: 'main' as const,
  version: 1,
  active: true,
  description: 'Step-only sales workflow for booked units.',
  steps: [
    { ...makeStep('Add customers'), key: 'add_customers', actionType: 'add_customer' },
    { ...makeStep('Add KYC documents'), key: 'add_kyc_documents', actionType: 'add_kyc', dependsOnSteps: ['add_customers'] },
    { ...makeStep('Upload signed booking form'), key: 'upload_signed_booking_form', actionType: 'upload_document', dependsOnSteps: ['add_customers'], docKey: 'booking_form_signed' },
    { ...makeStep('Add down payment receipt'), key: 'add_down_payment', actionType: 'add_payment', dependsOnSteps: ['add_customers'], paymentType: 'customer' },
    { ...makeStep('Add GST payment receipt'), key: 'add_gst_payment', actionType: 'add_payment', dependsOnSteps: ['add_down_payment'], required: false, allowSkip: true, paymentType: 'gst' },
    { ...makeStep('Generate allotment letter draft'), key: 'generate_allotment_letter_draft', actionType: 'generate_document', dependsOnSteps: ['add_down_payment'], docKey: 'allotment_letter_v1' },
    { ...makeStep('Send documents to advocate'), key: 'send_documents_to_advocate', actionType: 'toggle_yes_no', dependsOnSteps: ['generate_allotment_letter_draft', 'upload_signed_booking_form'], fieldKey: 'sentToAdvocate' },
  ] as WfStep[],
});

export const loanPreset = () => ({
  name: 'Loan Workflow (V2 - Step Only)',
  type: 'loan' as const,
  version: 1,
  active: true,
  description: 'Step-only loan workflow.',
  steps: [
    { ...makeStep('Send Lead to SBI'), key: 'send_lead_to_sbi' },
    { ...makeStep('Collect Documents for Loan'), key: 'collect_documents_for_loan', actionType: 'checklist', fieldKey: 'loan_docs_checklist' },
    { ...makeStep('Send Lead to Bank'), key: 'send_lead_to_bank', actionType: 'add_bank_lead', dependsOnSteps: ['send_lead_to_sbi', 'collect_documents_for_loan'] },
    { ...makeStep('Upload sanction letter'), key: 'upload_sanction_letter', actionType: 'loan_sanction_form', dependsOnSteps: ['send_lead_to_bank'], required: false, docKey: 'sanction_letter' },
  ] as WfStep[],
});

/** Inserts the sale deed and possession steps, same as the previous editor's "Add deed and possession flow". */
export function withDeedPossessionFlow(steps: WfStep[]): { steps: WfStep[]; focus: number } {
  const flow: Array<Partial<WfStep> & { key: string; title: string }> = [
    { key: 'send_deed_package', title: 'Send Sale Deed Package to Advocate', dependsOnSteps: [FULL_PAYMENT_DEP_KEY, 'upload_registered_agreement', 'sale_agreement_date', 'signed_allotment_letter'], defaultRole: 'legal', helpText: 'Send signed booking form, payment details PDF, registered sale agreement and buyer details.' },
    { key: 'upload_deed_draft', title: 'Upload Sale Deed Draft', actionType: 'upload_document', docKey: 'sale_deed_draft', dependsOnSteps: ['send_deed_package'], defaultRole: 'legal' },
    { key: 'verify_deed_draft', title: 'Verify Sale Deed Draft', dependsOnSteps: ['upload_deed_draft'], defaultRole: 'legal', helpText: 'Check buyer names, unit details, consideration and payment figures.' },
    { key: 'request_deed_charges', title: 'Request Sale Deed Charges from Customer', dependsOnSteps: ['send_deed_package'], defaultRole: 'accounts', helpText: 'Send the customer a charge breakup for stamp duty, registration and advocate deed fee.' },
    { key: 'deed_charges_collected', title: 'Confirm Deed Charges Collected', actionType: 'deed_charges_paid', dependsOnSteps: ['request_deed_charges'], defaultRole: 'accounts', helpText: 'Check stamp duty, registration charges and advocate deed fee receipts against the agreed charge breakup.' },
    { key: 'customer_signed_deed', title: 'Customer Signed Sale Deed', dependsOnSteps: [FULL_PAYMENT_DEP_KEY, 'verify_deed_draft', 'deed_charges_collected'], defaultRole: 'legal' },
    { key: 'advocate_registered_deed', title: 'Advocate Registered Sale Deed', dependsOnSteps: ['customer_signed_deed'], defaultRole: 'legal' },
    { key: 'fitout_access_letter', title: 'Upload Signed Fit-out Access Letter', actionType: 'upload_document', docKey: 'fitout_access_signed', dependsOnSteps: [PRE_BUC_DEP_KEY, 'sale_deed_upload'], defaultRole: 'legal', required: false, allowSkip: true, showInDashboard: false, helpText: 'Limited pre-BUC access for furniture or interior work only, subject to approved terms.' },
    { key: 'fitout_unit_checklist', title: 'Upload Unit Condition Checklist', actionType: 'upload_document', docKey: 'fitout_condition_checklist', dependsOnSteps: [PRE_BUC_DEP_KEY, 'fitout_access_letter'], defaultRole: 'sales-manager', required: false, allowSkip: true, showInDashboard: false, helpText: 'Record unit condition, photographs, meter readings and keys.' },
    { key: 'maintenance_cheque_received', title: 'Upload Six-month Maintenance Cheque', actionType: 'upload_document', docKey: 'maintenance_cheque', dependsOnSteps: [PRE_BUC_DEP_KEY, 'fitout_unit_checklist'], defaultRole: 'accounts', required: false, allowSkip: true, showInDashboard: false, helpText: 'Record cheque details in Accounts. Uploading it confirms receipt, not clearance.' },
    { key: 'fitout_access_issued', title: 'Fit-out Access Issued', dependsOnSteps: [PRE_BUC_DEP_KEY, 'maintenance_cheque_received'], defaultRole: 'sales-manager', required: false, allowSkip: true, showInDashboard: false },
    { key: 'final_possession', title: 'Upload Signed Final Possession Handover', actionType: 'upload_document', docKey: 'final_possession_ack', dependsOnSteps: [FULL_PAYMENT_DEP_KEY, BUC_DEP_KEY, 'sale_deed_upload'], defaultRole: 'sales-manager', helpText: 'Upload the signed handover after BUC covers this building. Soft access is optional.' },
  ];
  const keys = new Set(flow.map((f) => f.key));
  const base = steps.filter((s) => !keys.has(s.key));
  const add = flow.map((f) => ({ ...makeStep(f.title), ...f }) as WfStep);
  const after = ['fitout_access_letter', 'fitout_unit_checklist', 'maintenance_cheque_received', 'fitout_access_issued', 'final_possession'];
  const before = add.filter((s) => !after.includes(s.key));
  const later = add.filter((s) => after.includes(s.key));
  const deedAt = base.findIndex((s) => s.key === 'sale_deed_date');
  if (deedAt < 0) return { steps: [...base, ...before, ...later], focus: base.length };
  return {
    steps: [
      ...base.slice(0, deedAt),
      ...before,
      ...base.slice(deedAt).map((s) => (s.key === 'sale_deed_date' ? { ...s, dependsOnSteps: ['advocate_registered_deed'] } : s.key === 'sale_deed_upload' ? { ...s, dependsOnSteps: ['sale_deed_date', 'sale_deed_number'] } : s)),
      ...later,
    ],
    focus: deedAt,
  };
}
